import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authAttempted, setAuthAttempted] = useState(false);

  const authenticateAnonymously = useCallback(async () => {
    if (authAttempted) return;

    setAuthAttempted(true);
    try {
      const userCredential = await signInAnonymously(auth);
      const accessToken = await userCredential.user.getIdToken();
      localStorage.setItem('accessToken', accessToken);
    } catch (error) {
      console.error('Anonymous authentication failed:', error);
      setIsLoading(false); // Stop loading even on error
    }
  }, [authAttempted]);

  const refreshToken = useCallback(async (user: any) => {
    try {
      const token = await user.getIdToken(true);
      localStorage.setItem('accessToken', token);
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      if (user) {
        setUser(user);
        setIsAuthenticated(true);
        setIsLoading(false);
        refreshToken(user);
        setAuthAttempted(false);
      } else if (!authAttempted) {
        setIsAuthenticated(false);
        setUser(null);
        authenticateAnonymously();
      } else {
        setIsLoading(false);
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe();
  }, [authAttempted, authenticateAnonymously, refreshToken]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user }}>
      {!isLoading ? children : null}
    </AuthContext.Provider>
  );
};
